/**
 * 65.29 (2) (the owner, Oct 11: the rocket tube "is actually a pretty good part of the game … then probably also tie it
 * into a pooled team level resource system"), with GameConfig.rocketStock and the tube on (GameConfig.rockets): each
 * side's rockets come from a finite stock, on 65.23's pattern (core/grenadeStock.ts).
 * - A unit's issue is the tube's round and its spare (GROUND_WEAPONS.rocket: magazine 1, reserve 1); its side's stock
 *   (`state.rocketStocks`) starts at ROCKET_STOCK.refills issues for every unit the side starts with (whole rockets).
 * - A respawn — so a drop at a relay or a landing at a beacon — draws the unit's issue from the stock, as far as it goes
 *   (the tube's round first, then the spare); with none left it comes back with an empty tube.
 * - A team crate in reach tops the spare back up from the stock when the unit asks (the crate's own magazines no longer
 *   serve the tube: core/supply.ts leaves the rocket to the stock).
 * - The bots walk to a crate when they carry none and their side still has some, and with the stock they spend a rocket
 *   on a gunner in a seat or a vehicle's crew, not a man (a huddle of ACTION_PARAMS.rocket.stockHuddle still counts).
 * Pure; with the switch off no side has a stock and nothing here does anything.
 */
import type { GameState, PlayerState, GameConfig } from '../types';
import { GROUND_WEAPONS, ROCKET_STOCK } from '../constants/action';
import { nearestCrate, crateSide, supplyReach } from './supply';
import { standingCrate } from './grenadeStock';
import { servesPool } from './fob';

export type RocketEvent = { kind: 'rocketsRestocked'; unitId: number; rockets: number; at: 'respawn' | 'crate' };

const TUBE = GROUND_WEAPONS.rocket.magazine, SPARE = GROUND_WEAPONS.rocket.reserve ?? 0;
/** What a unit is issued: the tube's round and its spare. */
export const ROCKET_ISSUE = TUBE + SPARE;

/** The rockets a unit carries: in the tube and spare (absent = full, as the tube's spec reads it). */
export function rocketsCarried(p: Pick<PlayerState, 'magazines' | 'reserves'>): number {
  return (p.magazines?.rocket ?? TUBE) + (p.reserves?.rocket ?? SPARE);
}

/** S7: does a person of `team` carry the rocket tube — the switch on, and its side among `rocketTeams` (absent: every side). */
export function tubeFor(config: Pick<GameConfig, 'rockets' | 'rocketTeams'>, team: number): boolean {
  return config.rockets === true && (config.rocketTeams === undefined || config.rocketTeams.includes(team));
}

/** 65.32: a unit's own tube — on a side with a squad layout (GameConfig.layoutTeams), its slot's secondary; elsewhere every person of the side (absent layouts: true, as before). */
export function ownTube(unit: Pick<PlayerState, 'teamIndex' | 'secondary'>, config: Pick<GameConfig, 'layoutTeams'>): boolean {
  return !config.layoutTeams?.[unit.teamIndex] || unit.secondary === 'rocket';
}

/** 65.32: does this unit carry the rocket tube — its side's (tubeFor) and its own (ownTube); with the player's own loadout, that list's. */
export function carriesRocket(unit: Pick<PlayerState, 'teamIndex' | 'secondary'> & { carry?: PlayerState['carry'] }, config: Pick<GameConfig, 'rockets' | 'rocketTeams' | 'layoutTeams'>): boolean {
  if (unit.carry) return unit.carry.includes('rocket');   // (the player's own loadout: its own, whatever its side's)
  return tubeFor(config, unit.teamIndex) && ownTube(unit, config);
}

/** The setup's sizing (the tube and the switch on): each side's stock is `refills` issues for every unit it starts with (a side without the tube, none). */
export function withRocketStocks(state: GameState): GameState {
  if (!state.config.rockets) return state;
  const stocks: Record<number, number> = {};
  for (const p of state.players) if (carriesRocket(p, state.config)) stocks[p.teamIndex] = (stocks[p.teamIndex] ?? 0) + ROCKET_ISSUE * ROCKET_STOCK.refills;
  for (const t of Object.keys(stocks)) stocks[Number(t)] = Math.floor(stocks[Number(t)]);   // whole rockets
  return Object.keys(stocks).length ? { ...state, rocketStocks: stocks } : state;
}

/** A respawned unit's issue drawn from its side's stock: the tube's round first, then the spare. The event says what it got (none: no event, an empty tube). */
export function issueRockets(state: GameState, unitId: number): { state: GameState; event: RocketEvent | null } {
  const p = state.players.find(x => x.id === unitId);
  const stock = p ? state.rocketStocks?.[p.teamIndex] : undefined;
  if (!p || stock === undefined || !ownTube(p, state.config)) return { state, event: null };
  const got = Math.min(ROCKET_ISSUE, stock), tube = Math.min(TUBE, got), spare = got - tube;
  const players = state.players.map(x => (x.id === unitId ? { ...x, magazines: { ...(x.magazines ?? {}), rocket: tube }, reserves: { ...(x.reserves ?? {}), rocket: spare } } : x));
  return { state: { ...state, players, rocketStocks: { ...state.rocketStocks, [p.teamIndex]: stock - got } }, event: got > 0 ? { kind: 'rocketsRestocked', unitId, rockets: got, at: 'respawn' } : null };
}

/** Carrying none, and the side's stock still holds some: worth a walk to a crate. */
export function rocketsOut(state: Pick<GameState, 'rocketStocks'>, p: Pick<PlayerState, 'magazines' | 'reserves' | 'teamIndex'>): boolean {
  return (state.rocketStocks?.[p.teamIndex] ?? 0) > 0 && rocketsCarried(p) <= 0;
}

/** Short of the spare, and the side's stock still holds some: worth asking for at a crate in reach. */
export function rocketsWanted(state: Pick<GameState, 'rocketStocks'>, p: Pick<PlayerState, 'reserves' | 'teamIndex'>): boolean {
  return (state.rocketStocks?.[p.teamIndex] ?? 0) > 0 && (p.reserves?.rocket ?? SPARE) < SPARE;
}

/** One tick's crate top-ups of the spare from the stock: every unit asking, on its feet and out of any seat, within reach of a team crate. Null when nothing changed. */
export function stepRocketRestock(state: GameState, asks: ReadonlyArray<boolean | undefined>): { state: GameState; events: RocketEvent[] } | null {
  if (!state.rocketStocks) return null;
  let next = state;
  const events: RocketEvent[] = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = next.players[i];
    if (!asks[i] || !p.alive || p.airborne || p.mount) continue;
    const crate = nearestCrate(next, p, supplyReach(next), s => standingCrate(s) && servesPool(next, s));   // 69.4: a FOB only while linked
    const side = crate ? crateSide(next, crate, p) : p.teamIndex;   // (242: any side's crate, its side's stock)
    if (!crate || !rocketsWanted(next, { reserves: p.reserves, teamIndex: side })) continue;
    const stock = next.rocketStocks![side], give = Math.min(SPARE - (p.reserves?.rocket ?? SPARE), stock);
    next = {
      ...next,
      players: next.players.map(x => (x.id === p.id ? { ...x, reserves: { ...(x.reserves ?? {}), rocket: (x.reserves?.rocket ?? SPARE) + give } } : x)),
      rocketStocks: { ...next.rocketStocks, [side]: stock - give },
    };
    events.push({ kind: 'rocketsRestocked', unitId: p.id, rockets: give, at: 'crate' });
  }
  return events.length ? { state: next, events } : null;
}
